import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { salesService } from '../../services/salesService';
import { contactsService } from '../../services/contactsService';
import { paymentsService } from '../../services/paymentsService';

export default function BulkPaymentModal({ isOpen, onClose, contact, onPaymentComplete }) {
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [pendingOrders, setPendingOrders] = useState([]);
  const totalDues = contact?.balance || 0;
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPendingOrders = async () => {
      if (contact?.id) {
        const orders = await salesService.getOrdersByCustomer(contact.id);
        const pending = orders
          .filter(o => (o.remainingBalance || 0) > 0)
          .sort((a, b) => new Date(a.finalizedAt) - new Date(b.finalizedAt));
        setPendingOrders(pending);
      }
    };
    
    if (isOpen) {
      fetchPendingOrders();
      setAmount('');
    }
  }, [contact?.id, isOpen]);

  const validateAmount = (value) => {
    if (!value) {
      setError('Amount is required');
      return false;
    }
    const numValue = Number(value);
    if (isNaN(numValue) || numValue <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    if (numValue > totalDues) {
      setError('Amount cannot exceed total dues');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAmount(amount)) return;
    setLoading(true);

    try {
      const paymentAmount = Number(amount);
      
      const updatedOrders = await salesService.updateOrderWithBulkPayment(null, {
        amount: paymentAmount
      }, contact.id);

      // Record the bulk payment
      await paymentsService.recordPayment({
        customerId: contact.id,
        customerName: contact.name,
        amount: paymentAmount,
        type: 'bulk_payment',
        orderDetails: {
          orders: updatedOrders.map(order => ({
            orderId: order.id,
            items: order.items,
            amountApplied: Math.min(paymentAmount, order.remainingBalance),
            remainingBalance: Math.max(0, order.remainingBalance - paymentAmount)
          }))
        }
      });

      const updatedContact = await contactsService.getContactDetails(contact.id);
      onPaymentComplete(updatedContact);
      onClose();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Make Payment">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
            <span>Total Outstanding Amount:</span>
            <span className="font-medium text-red-600 dark:text-red-400">
              ₹{totalDues.toLocaleString()}
            </span>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Payment Amount
          </label>
          <div className="mt-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">₹</span>
              </div>
              <input
                type="text"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setAmount(value);
                    validateAmount(value);
                  }
                }}
                className={`block w-full pl-7 pr-12 py-2.5 sm:text-sm rounded-lg
                  border-2 transition-all duration-200 ease-in-out
                  bg-white dark:bg-gray-800
                  ${error 
                    ? 'border-red-300 text-red-900 dark:text-red-400 placeholder-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:border-primary-500 dark:focus:border-primary-400'
                  }
                  focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50
                  ${error 
                    ? 'focus:ring-red-200 dark:focus:ring-red-800' 
                    : 'focus:ring-primary-200 dark:focus:ring-primary-800'
                  }
                  placeholder-gray-400 dark:placeholder-gray-500`}
                placeholder="0.00"
                required
              />
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !amount}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
              rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Make Payment'}
          </button>
        </div>
      </form>
    </Modal>
  );
} 